import { memo, useRef, useState, useEffect, useSyncExternalStore } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { useSegmentTransition } from '../../hooks/useSegmentTransition';
import AddSegmentMenuOptions from './AddSegmentMenuOptions';
import AddSegmentUploadView from './AddSegmentUploadView';
import { useAddSegmentUpload } from './useAddSegmentUpload';
import type { AddMenuView, AddSegmentButtonProps } from './types';
import { FeatureFlagKeys } from '@/services/featureFlag';
import Icon from '@/components/atoms/Icon';
import TooltipV2 from '@/components/TooltipV2';
import featureFlagStore from '@/stores/featureFlagStore';

function AddSegmentButton({
  previousSegment,
  nextSegment = null,
  isTransitionApplied = false,
  showUploadOption = true
}: AddSegmentButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<AddMenuView>('menu');
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isSegmentTransitionsEnabled = featureFlags[FeatureFlagKeys.Use_CL_Segment_Transitions];

  const upload = useAddSegmentUpload(previousSegment);
  const { toggleTransition, canToggle: canToggleTransition } = useSegmentTransition({
    previousSegment,
    nextSegment,
    isTransitionApplied
  });

  const showMenu = isSegmentTransitionsEnabled;

  const updatePopoverPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.top - 8,
        left: rect.left + rect.width / 2
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePopoverPosition();
      const handleScroll = () => updatePopoverPosition();
      const handleResize = () => updatePopoverPosition();
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen]);

  const handleClose = (close: () => void) => () => {
    close();
    setIsOpen(false);
    setView('menu');
  };

  const tooltipId = `add-segment-button-${previousSegment?.id ?? 'start'}-${nextSegment?.id ?? 'end'}`;

  return (
    <Popover>
      {({ close }) => (
        <>
          <TooltipV2
            tooltipId={tooltipId}
            tooltipContent="Add..."
            noArrow
            place="top"
            className="z-50"
            offset={25}
            tooltipElement={
              <Popover.Button
                ref={buttonRef}
                onClick={() => {
                  setView(showMenu ? 'menu' : 'upload');
                  setIsOpen(true);
                  updatePopoverPosition();
                }}
                className="absolute left-1/2 top-1/2 z-50 flex size-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-lg border-2 border-slate-300 bg-slate-200 opacity-0 transition-opacity duration-200 hover:border-slate-400 hover:bg-slate-300 group-hover:opacity-100"
              >
                <Icon name="IconPlus" width={16} height={16} className="text-slate-600" />
              </Popover.Button>
            }
          />
          <Transition
            appear
            show={isOpen}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
            afterLeave={() => setIsOpen(false)}
          >
            <Popover.Panel
              static
              className={`fixed z-[60] rounded-xl border bg-white shadow-lg ${view === 'menu' ? 'w-48' : 'w-[382px]'}`}
              style={{
                top: `${popoverPosition.top}px`,
                left: `${popoverPosition.left}px`,
                transform: 'translate(-50%, -100%)'
              }}
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
            >
              <div className={`max-h-[500px] overflow-y-auto ${view === 'menu' ? 'p-2' : 'px-4 py-3'}`}>
                {view === 'menu' && (
                  <AddSegmentMenuOptions
                    onUploadFileClick={() => setView('upload')}
                    onTransitionClick={() => toggleTransition(handleClose(close))}
                    isTransitionApplied={isTransitionApplied}
                    canToggleTransition={canToggleTransition}
                    showUploadOption={showUploadOption}
                  />
                )}
                {view === 'upload' && (
                  <AddSegmentUploadView
                    onClose={handleClose(close)}
                    clipIntroOutro={upload.clipIntroOutro}
                    brandKitIntroOutro={upload.brandKitIntroOutro}
                    brandKitStore={upload.brandKitStore}
                    uploadFiles={upload.uploadFiles}
                    onFilesUpload={upload.onFilesUpload}
                    handleVideoSelect={upload.handleVideoSelect}
                    deleteItem={upload.deleteItem}
                    removedBrandKitName={upload.removedBrandKitName}
                  />
                )}
              </div>
            </Popover.Panel>
          </Transition>
          {isOpen && (
            <div
              className="fixed inset-0 z-50"
              onClick={() => {
                setView('menu');
                setIsOpen(false);
                close();
              }}
            />
          )}
        </>
      )}
    </Popover>
  );
}

export default memo(AddSegmentButton);
